import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { ReactComponent as ArrowIcon } from '../../../assets/arrow-pagination.svg';
import { DeliveryMethod } from '../../../models/delivery-options/common';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import {
  MiddleMileFilterHeader,
  MiddleMileFiltersContainer,
  TabsNavItem,
} from './middle-mile-styles';
import MiddleMileFilterOption from './middle-mile-filter-option.component';
import { DeliveryOption } from '../../../models/delivery-options/delivery-option';
import { FILTER_ORDER, getFastestByType } from '../helpers';
import { useStore } from '../../../stores/store';
import { ServicesSubSectionHeader } from '../../../features/services/services-subsection/services-subsection.styles';
import { InfoButtonContainer } from '../parcel-delivery.styles';
import { ReactComponent as InfoIconDeliveryOptions } from '../../../assets/info-tip.svg';

interface Props {
  deliveryOptions: DeliveryOption[] | undefined;
  isAnimationTriggered?: boolean | undefined;
  onFilterSelected: (filterType: DeliveryMethod) => void;
  activeFilter: DeliveryMethod | undefined;
}

const MiddleMileFilter = ({
  deliveryOptions,
  onFilterSelected,
  activeFilter,
  isAnimationTriggered,
}: Props) => {
  const {
    parcelCreationStore: { selectedMiddleMileFilter },
    navStore: { toggleSidebarByName },
  } = useStore();
  const filtersRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const isMobile = useIsMobile();

  const { t } = useTranslation();

  const scrollLeft = () => {
    filtersRef.current?.scrollBy({ left: -80, behavior: 'smooth' });
  };

  const scrollRight = () => {
    filtersRef.current?.scrollBy({ left: 80, behavior: 'smooth' });
  };

  const scrollIntoViewIfNeeded = (element: HTMLElement) => {
    const container = filtersRef.current;
    if (!container || !element) return;

    const elLeftScroll = element.offsetLeft - 10;
    const elRight = element.offsetLeft + element.offsetWidth + 10;

    const containerLeft = container.scrollLeft;
    const containerRight = containerLeft + container.clientWidth;

    if (element.offsetLeft < containerLeft) {
      container.scrollTo({ left: elLeftScroll, behavior: 'smooth' });
    } else if (elRight > containerRight) {
      container.scrollTo({
        left: elRight - container.clientWidth,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const el = filtersRef.current;
    if (!el) return;

    const handleScroll = () => {
      setIsAtStart(el.scrollLeft <= 10);
      setIsAtEnd(el.scrollWidth - el.scrollLeft - el.clientWidth <= 10);
    };

    el.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  const availableFilters = new Set(
    deliveryOptions?.map((item) => item.middle_mile.delivery_method)
  );

  const availableFiltersOrdered: DeliveryMethod[] = [
    ...FILTER_ORDER.filter((type) => availableFilters.has(type)),
  ];

  if (availableFiltersOrdered.length === 1 && selectedMiddleMileFilter) {
    return undefined;
  }

  return (
    <>
      <MiddleMileFilterHeader
        $align='center'
        $justify='start'
        $mtMobile='1.6rem'
        $mb='1.2rem'
        $gap='.8rem'
        $isMiddleMileSelected={!!selectedMiddleMileFilter}>
        <ServicesSubSectionHeader>
          {t('delivery_options_filters_header_choose_service')}
        </ServicesSubSectionHeader>
        <InfoButtonContainer
          onClick={() => toggleSidebarByName('delivery_options_hints')}>
          <InfoIconDeliveryOptions />
        </InfoButtonContainer>
      </MiddleMileFilterHeader>
      {isMobile && availableFiltersOrdered.length > 3 && (
        <>
          {!isAtStart && (
            <TabsNavItem
              className='tab-nav-item-prev'
              onClick={scrollLeft}>
              <ArrowIcon />
            </TabsNavItem>
          )}
          {!isAtEnd && (
            <TabsNavItem
              className='tab-nav-item-next'
              onClick={scrollRight}>
              <ArrowIcon />
            </TabsNavItem>
          )}
        </>
      )}
      <MiddleMileFiltersContainer
        $numOfCards={availableFiltersOrdered.length}
        $isMiddleMileSelected={!!selectedMiddleMileFilter}
        $isAnimationTriggered={isAnimationTriggered}
        ref={filtersRef}>
        {availableFiltersOrdered.map((x) => (
          <MiddleMileFilterOption
            key={x}
            deliveryOption={getFastestByType(deliveryOptions, x)}
            isActive={x === activeFilter}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              onFilterSelected(x);
              scrollIntoViewIfNeeded(e.currentTarget as HTMLElement);
            }}
          />
        ))}
      </MiddleMileFiltersContainer>
    </>
  );
};

export default observer(MiddleMileFilter);
